import { Link } from 'react-router-dom';

import {
  Badge,
  Box,
  FileInlineCard,
  FileInlineCardIcon,
  FileInlineCardLabel,
  FileProvider,
  HStack,
  Text,
  VStack,
} from '@frontend/legacy-components';

import type { FindingFileReference } from './types';

type FindingProvenanceProps = {
  fileReferences: FindingFileReference[];
};

const fileTypeFromFilename = (filename: string) =>
  filename.split('.').pop()?.toLowerCase() || 'file';

const badgeProps = {
  size: 'xs',
  variant: 'subtle',
  colorScheme: 'assistance',
  bg: 'page.plateMedium',
  borderColor: 'page.plateLight',
  color: 'feature.primary',
} as const;

const FileReferenceCardContent = ({
  badgeValue,
  fileRef,
}: {
  badgeValue: number | null;
  fileRef: FindingFileReference;
}) => (
  <>
    <FileInlineCardIcon />
    <FileInlineCardLabel />
    {badgeValue != null && (
      <Badge {...badgeProps} ml={0.25}>
        {badgeValue}
      </Badge>
    )}
    {fileRef.pageNumber != null && (
      <>
        <Box
          borderLeft="1px solid"
          borderColor="separator.divider"
          alignSelf="stretch"
          mx={0.5}
        />
        <Text fontSize="sm" color="feature.secondary">
          Page {fileRef.pageNumber}
        </Text>
      </>
    )}
  </>
);

const FileReferenceCard = ({ fileRef }: { fileRef: FindingFileReference }) => {
  const fileProviderProps = {
    filename: fileRef.filename,
    fileSize: 0,
    fileType: fileTypeFromFilename(fileRef.filename),
    uploaded: '',
    uuid: fileRef.partFileUuid,
  };

  const badgeValue =
    fileRef.count != null && fileRef.count !== 1 ? fileRef.count : null;

  return (
    <FileProvider {...fileProviderProps}>
      {fileRef.href ? (
        // If the file has a href, it is a link to the file
        <FileInlineCard
          as={Link}
          to={fileRef.href}
          cursor="pointer"
          target="_blank"
          rel="noopener noreferrer"
          outline="none"
          onClick={(e) => {
            if (fileRef.onFileClick) {
              e.preventDefault();
              fileRef.onFileClick();
            }
          }}
          display="inline-flex"
        >
          <FileReferenceCardContent badgeValue={badgeValue} fileRef={fileRef} />
        </FileInlineCard>
      ) : fileRef.onFileClick ? (
        // If the file has a onFileClick, it is a button that will open the file
        <FileInlineCard
          as="button"
          cursor="pointer"
          onClick={fileRef.onFileClick}
          outline="none"
          display="inline-flex"
        >
          <FileReferenceCardContent badgeValue={badgeValue} fileRef={fileRef} />
        </FileInlineCard>
      ) : (
        // If the file has no href or onFileClick, it is a static file
        <FileInlineCard display="inline-flex" outline="none">
          <FileReferenceCardContent badgeValue={badgeValue} fileRef={fileRef} />
        </FileInlineCard>
      )}
    </FileProvider>
  );
};

/** A component that displays the "provenance" of a finding, i.e. the files that finding is associated with,
 * as a list of inline cards with or without click behavior
 */
export const FindingProvenance = ({
  fileReferences,
}: FindingProvenanceProps) => {
  return (
    <VStack
      borderTop="1px solid"
      borderColor="separator.divider"
      align="stretch"
      gap={0.25}
      px={1.5}
      py={1}
    >
      <HStack spacing={0.5} alignItems="center" flexWrap="wrap" gap={0.25}>
        {fileReferences.map((ref) => (
          <FileReferenceCard key={ref.partFileUuid} fileRef={ref} />
        ))}
      </HStack>
    </VStack>
  );
};
